/* eslint-disable max-lines -- legacy size; refactor planned in plans/cached-singing-fiddle.md */
import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

// --- Token prefix routing ---

export const INVITE_PREFIXES = {
  workspace: "ws_",
  channel: "ch_",
  client_space: "cs_",
  dm: "dm_",
  group_dm: "gdm_",
} as const;

export type InviteType = keyof typeof INVITE_PREFIXES;

export function detectInviteType(token: string): InviteType | null {
  if (token.startsWith(INVITE_PREFIXES.group_dm)) return "group_dm";
  if (token.startsWith(INVITE_PREFIXES.dm)) return "dm";
  if (token.startsWith(INVITE_PREFIXES.workspace)) return "workspace";
  if (token.startsWith(INVITE_PREFIXES.channel)) return "channel";
  if (token.startsWith(INVITE_PREFIXES.client_space)) return "client_space";
  return null; // legacy unprefixed token
}

// --- Resolver response (discriminated union) ---

export const workspaceInviteInfoSchema = z.object({
  type: z.literal("workspace"),
  workspace_id: z.string(),
  workspace_name: z.string(),
  workspace_logo_url: z.string().nullable().optional(),
  inviter_name: z.string().nullable().optional(),
  inviter_avatar_url: z.string().nullable().optional(),
});

export const channelInviteInfoSchema = z.object({
  type: z.literal("channel"),
  workspace_id: z.string(),
  workspace_name: z.string(),
  channel_name: z.string(),
  workspace_logo_url: z.string().nullable().optional(),
  inviter_name: z.string().nullable().optional(),
  inviter_avatar_url: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
});

export const clientSpaceInviteInfoSchema = z.object({
  type: z.literal("client_space"),
  space_name: z.string(),
  workspace_id: z.string(),
  workspace_name: z.string(),
  workspace_logo_url: z.string().nullable().optional(),
  channel_count: z.number(),
  member_count: z.number(),
  scope: z.enum(["individual", "workspace"]).optional(),
  inviter_name: z.string().nullable().optional(),
  inviter_avatar_url: z.string().nullable().optional(),
});

export const dmInviteInfoSchema = z.object({
  type: z.literal("dm"),
  inviter_name: z.string(),
  inviter_avatar_url: z.string().nullable().optional(),
});

export const groupDmInviteInfoSchema = z.object({
  type: z.literal("group_dm"),
  group_name: z.string().nullable().optional(),
  member_count: z.number(),
  inviter_name: z.string(),
  inviter_avatar_url: z.string().nullable().optional(),
});

export const inviteInfoSchema = z.discriminatedUnion("type", [
  workspaceInviteInfoSchema,
  channelInviteInfoSchema,
  clientSpaceInviteInfoSchema,
  dmInviteInfoSchema,
  groupDmInviteInfoSchema,
]);

export type InviteInfo = z.infer<typeof inviteInfoSchema> & {
  /** Only present when resolved with an authenticated request */
  invited_email?: string | null;
};

// --- Accept response ---

export const inviteAcceptSuccessSchema = z.object({
  workspace_id: z.string().optional(),
  joined: z.boolean().optional(),
  already_member: z.boolean().optional(),
  space_id: z.string().optional(),
  channel_id: z.string().optional(),
  channels_joined: z.number().optional(),
  workspace_linked: z.boolean().optional(),
});

export type InviteAcceptSuccess = z.infer<typeof inviteAcceptSuccessSchema>;

// --- Workspace create request ---

export const createWorkspaceInviteBodySchema = z.object({
  // `.min(1)` mirrors the DB column (`workspaces.id TEXT PRIMARY KEY`) and
  // the sibling `acceptWorkspaceInviteBodySchema`. Production workspaces
  // happen to be UUIDs (server-generated via randomUUID) but the contract
  // is "non-empty string" — non-UUID IDs like `local-dev-workspace` are
  // legitimate seed data.
  workspace_id: z.string().min(1),
  invited_email: z.string().email().optional(),
  expires_in_hours: z.number().int().min(1).max(720).optional(),
});

export const createWorkspaceInviteSuccessSchema = z.object({
  token: z.string(),
  invite_url: z.string(),
  expires_at: z.string().optional(),
});

export type CreateWorkspaceInviteBody = z.infer<
  typeof createWorkspaceInviteBodySchema
>;
export type CreateWorkspaceInviteSuccess = z.infer<
  typeof createWorkspaceInviteSuccessSchema
>;

// --- DM create request ---

export const createDmInviteBodySchema = z
  .object({
    other_user_id: z.string().min(1).optional(),
    invited_email: z.string().email().optional(),
    expires_in_hours: z.number().int().min(1).max(720).optional(),
  })
  .refine((d) => d.other_user_id || d.invited_email, {
    message: "Either other_user_id or invited_email is required",
  });

export type CreateDmInviteBody = z.infer<typeof createDmInviteBodySchema>;

// --- Group DM create request ---

export const createGroupDmInviteBodySchema = z.object({
  channel_id: z.string().min(1),
  max_uses: z.number().int().min(1).max(1000).optional(),
  expires_in_hours: z.number().int().min(1).max(720).optional(),
  invited_email: z.string().email().optional(),
});

export type CreateGroupDmInviteBody = z.infer<
  typeof createGroupDmInviteBodySchema
>;

// --- Channel invite create request ---

export const createChannelInviteBodySchema = z.object({
  channel_id: z.string().min(1),
  max_uses: z.number().int().min(1).max(1000).optional(),
  expires_in_hours: z.number().int().min(1).max(720).optional(),
  invited_email: z.string().email().optional(),
});

export type CreateChannelInviteBody = z.infer<
  typeof createChannelInviteBodySchema
>;

// --- Client space invite create request ---

export const createClientSpaceInviteBodySchema = z.object({
  space_id: z.string().min(1),
  max_uses: z.number().int().min(1).max(1000).optional(),
  expires_in_hours: z.number().int().min(1).max(720).optional(),
  invited_email: z.string().email().optional(),
  invited_user_id: z.string().min(1).optional(),
  scope: z.enum(["individual", "workspace"]).optional(),
});

export type CreateClientSpaceInviteBody = z.infer<
  typeof createClientSpaceInviteBodySchema
>;

// --- Path builders ---

export function inviteInfoPath(token: string): string {
  return `/api/invites/${encodeURIComponent(token)}`;
}

export function inviteAcceptPath(token: string): string {
  return `/api/invites/${encodeURIComponent(token)}/accept`;
}

export function createWorkspaceInvitePath(): string {
  return "/api/invites/workspace";
}

export function createDmInvitePath(): string {
  return "/api/invites/dm";
}

export function createGroupDmInvitePath(): string {
  return "/api/invites/group-dm";
}

export function createChannelInvitePath(): string {
  return "/api/invites/channel";
}

export function createClientSpaceInvitePath(): string {
  return "/api/invites/client-space";
}

export function inviteAcceptWorkspacePath(token: string): string {
  return `/api/invites/${encodeURIComponent(token)}/accept-workspace`;
}

export const acceptWorkspaceInviteBodySchema = z.object({
  workspace_id: z.string().min(1),
});

export type AcceptWorkspaceInviteBody = z.infer<
  typeof acceptWorkspaceInviteBodySchema
>;

// --- Client request helpers ---

function buildAuthHeaders(accessToken?: string): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

export async function getInviteInfoRequest({
  apiBaseUrl,
  token,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  token: string;
  /** If provided, server includes invited_email for mismatch detection */
  accessToken?: string;
  fetchImpl?: FetchLike;
}): Promise<InviteInfo> {
  const fetchFn = fetchImpl ?? fetch;
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const url = `${normalizeApiBaseUrl(apiBaseUrl)}${inviteInfoPath(token)}`;
  const response = accessToken
    ? await fetchFn(url, { headers })
    : await fetchFn(url);

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to get invite info");
  }

  const payload = await response.json().catch(() => null);
  const parsed = inviteInfoSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid invite info response", 500, {
      issues: parsed.error.issues,
    });
  }

  // invited_email is an extra field not in the discriminated union schema
  const invitedEmail =
    payload && typeof payload === "object" && "invited_email" in payload
      ? (payload.invited_email as string | null)
      : undefined;

  return { ...parsed.data, invited_email: invitedEmail };
}

export async function acceptInviteRequest({
  apiBaseUrl,
  token,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  token: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<InviteAcceptSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${inviteAcceptPath(token)}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to accept invite");
  }

  const payload = await response.json().catch(() => null);
  const parsed = inviteAcceptSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid accept response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

export async function createDmInviteRequest({
  apiBaseUrl,
  otherUserId,
  invitedEmail,
  expiresInHours,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  otherUserId?: string;
  invitedEmail?: string;
  expiresInHours?: number;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<CreateWorkspaceInviteSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const body = createDmInviteBodySchema.parse({
    other_user_id: otherUserId,
    invited_email: invitedEmail,
    expires_in_hours: expiresInHours,
  });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${createDmInvitePath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to create DM invite");
  }

  const payload = await response.json().catch(() => null);
  const parsed = createWorkspaceInviteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid create invite response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

export async function createGroupDmInviteRequest({
  apiBaseUrl,
  channelId,
  maxUses,
  expiresInHours,
  invitedEmail,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  channelId: string;
  maxUses?: number;
  expiresInHours?: number;
  invitedEmail?: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<CreateWorkspaceInviteSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const body = createGroupDmInviteBodySchema.parse({
    channel_id: channelId,
    max_uses: maxUses,
    expires_in_hours: expiresInHours,
    invited_email: invitedEmail,
  });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${createGroupDmInvitePath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to create group DM invite",
    );
  }

  const payload = await response.json().catch(() => null);
  const parsed = createWorkspaceInviteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid create invite response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

export async function createWorkspaceInviteRequest({
  apiBaseUrl,
  workspaceId,
  invitedEmail,
  expiresInHours,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  workspaceId: string;
  invitedEmail?: string;
  expiresInHours?: number;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<CreateWorkspaceInviteSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const body = createWorkspaceInviteBodySchema.parse({
    workspace_id: workspaceId,
    invited_email: invitedEmail,
    expires_in_hours: expiresInHours,
  });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${createWorkspaceInvitePath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to create workspace invite",
    );
  }

  const payload = await response.json().catch(() => null);
  const parsed = createWorkspaceInviteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid create invite response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

export async function createChannelInviteRequest({
  apiBaseUrl,
  channelId,
  maxUses,
  expiresInHours,
  invitedEmail,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  channelId: string;
  maxUses?: number;
  expiresInHours?: number;
  invitedEmail?: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<CreateWorkspaceInviteSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const body = createChannelInviteBodySchema.parse({
    channel_id: channelId,
    max_uses: maxUses,
    expires_in_hours: expiresInHours,
    invited_email: invitedEmail,
  });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${createChannelInvitePath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to create channel invite");
  }

  const payload = await response.json().catch(() => null);
  const parsed = createWorkspaceInviteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid create invite response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

export async function createClientSpaceInviteRequest({
  apiBaseUrl,
  spaceId,
  maxUses,
  expiresInHours,
  invitedEmail,
  invitedUserId,
  scope,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  spaceId: string;
  maxUses?: number;
  expiresInHours?: number;
  invitedEmail?: string;
  invitedUserId?: string;
  scope?: "individual" | "workspace";
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<CreateWorkspaceInviteSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const body = createClientSpaceInviteBodySchema.parse({
    space_id: spaceId,
    max_uses: maxUses,
    expires_in_hours: expiresInHours,
    invited_email: invitedEmail,
    invited_user_id: invitedUserId,
    scope,
  });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${createClientSpaceInvitePath()}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to create client space invite",
    );
  }

  const payload = await response.json().catch(() => null);
  const parsed = createWorkspaceInviteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid create invite response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}

export async function acceptWorkspaceInviteRequest({
  apiBaseUrl,
  token,
  workspaceId,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  token: string;
  workspaceId: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<InviteAcceptSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${inviteAcceptWorkspacePath(token)}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify({ workspace_id: workspaceId }),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to accept workspace invite",
    );
  }

  const payload = await response.json().catch(() => null);
  const parsed = inviteAcceptSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid accept workspace response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
